import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import commonActions from "../../Redux/common/actions";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from "@mui/material";
import SecondaryButton from "../../common/secondaryButton";
import PrimaryButton from "../../common/primaryButton";
import { useNavigate } from "react-router-dom";
import examActions from "../../Redux/exam/actions";
import {
  handleDecodeJSON,
  handleEncodeJson,
} from "../../utils/encodeDecodeJson";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import axios from "axios";
import useFetchCandidateTokenDetails from "../../hooks/useFetchCandidateTokenDetails";
import { auditLog } from "../../utils/auditLog";
import { API_URL } from "../../utils/constants";

export default function SubmitModal({
  type,
  encryptedParams,
  handleNext,
  heartBeat,
  isMovementRestrictionWithNoTimeRestriction,
  currentSectionTitle
}) {
  // console.log(encryptedParams,'dddddd');
  
  // State declerations
  const [candidateTokenDetails, setCandidateTokenDetails] = useState(null);

  // Derieved variables
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showModal } = useSelector((state) => state.commonReducer);
  const { projectDetails } = useSelector((state) => state.examReducer);

  // Side effects
  useFetchCandidateTokenDetails({ setCandidateTokenDetails });

  // GET_PROJECT_DETAILS is already fetched by the live-test page; this component is mounted 3x
  // (submit/inactive/nextsection variants), so re-fetching here tripled the redundant API calls.

  // Helper functions
  const handleClose = () => {
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: "S", data: null },
    });
  };

  const handleSummary = () => {
    navigate(`/summary?qNo=${encryptedParams}`);
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: "S", data: null },
    });
  };

  const handleSubmit = async () => {
    if (projectDetails?.mockMode) {
      navigate("/end-exam");
    } else {
      try {
        const result = await axios.post(`${API_URL}/api/exam/end-exam`);
        const uploadData = result?.data?.data?.data;

        if (
          uploadData?.allowUpload === true &&
          uploadData?.uploadType === "END_EXAM_UPLOAD" &&
          uploadData?.token
        ) {
          // Redirect to post-exam upload screen with the upload session token
          const project = Cookies.get("test_engine_project");
          navigate(
            `/${project}/post-exam-upload?${uploadData.token}`
          );
        } else {
          navigate("/thank-you");
        }
      } catch {
        // On any error fall back to thank-you page
        navigate("/thank-you");
      }
    }
    auditLog({
      data: {
        RegistrationNumber: `${candidateTokenDetails?.user?.id}`,
        RollNumber: `${candidateTokenDetails?.user?.rollNo}`,
        Activity: "User selected in Summary Confirmation Submit",
        IPaddress: "",
      },
      dispatch,
      heartBeat,
    });
  };

  const handleYes = async () => {
    const now = Date.now();
    const currentTimestamp = dayjs();
    const formattedTimestamp = currentTimestamp.format(
      "YYYY-MM-DDTHH:mm:ss[Z]"
    );
    const data = {
      ipAddress: candidateTokenDetails?.user?.rollNo,
      macAddress: candidateTokenDetails?.user?.candidateName,
      Timestamp: formattedTimestamp,
      loginSessionId: sessionStorage.getItem("loginSessionId"),
    };
    const encryptedData = await handleEncodeJson({ encodeJSON: data });
    dispatch({
      type: examActions.ACTIVE_PING,
      payload: { data: encryptedData, lastApiCallTime: now },
    });
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: "S", data: null },
    });
  };

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalMinutes: 0,
  });

  const examStartTime = sessionStorage.getItem("examStartTime");
  const isStartExamBetweenSpecifiedTime =
    candidateTokenDetails?.configuration?.startExamBetweenSpecifiedTime;
  const examEndTime = sessionStorage.getItem("examEndTime");
  const examEndDate = new Date(examEndTime);
  examEndDate.setTime(examEndDate.getTime() + (5 * 60 + 30) * 60 * 1000);
  const updatedExamEndTime = examEndDate.toISOString();

  useEffect(() => {
    const startTime = Date.parse(
      isStartExamBetweenSpecifiedTime
        ? examStartTime
        : candidateTokenDetails?.configuration?.startTime
    );
    const endTime =
      // ((isLateLoginAllowed && isMaximumLoginAttempts ) || (isMultidayExamWithSection && isMaximumLoginAttempts))
      //   ? Date.parse(candidateTokenDetails?.configuration?.endTime)
      //   :
      Date.parse(
        updatedExamEndTime ?? candidateTokenDetails?.configuration?.endTime
      );

    const updateTimer = () => {
      const currentTime = new Date(heartBeat?.data);

      if (currentTime < startTime) {
        const totalDuration = endTime - startTime;
        const hours = Math.floor(totalDuration / (1000 * 60 * 60));
        const minutes = Math.floor(
          (totalDuration % (1000 * 60 * 60)) / (1000 * 60)
        );
        const totalSeconds = Math.floor(totalDuration / 1000);
        const seconds = totalSeconds % 60;
        setTimeLeft({ hours, minutes, seconds });
        return;
      }

      if (currentTime >= endTime) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      if (currentTime >= startTime) {
        const difference = endTime - currentTime;
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const totalSeconds = Math.floor(difference / 1000);
        const seconds = totalSeconds % 60;
        const totalMinutes = Math.floor(difference / (1000 * 60));
        setTimeLeft({ hours, minutes, seconds, totalMinutes });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [candidateTokenDetails, heartBeat]);

  async function handleYesNextSection() {
    if (!isMovementRestrictionWithNoTimeRestriction) {
      const encryptedData = sessionStorage.getItem("candidate_exam_details");
      const convertedEncryptedData = JSON.parse(encryptedData);
      const decodedData = await handleDecodeJSON({
        decodeJSON: convertedEncryptedData,
      });

      if (
        !decodedData ||
        !decodedData.configuration ||
        !decodedData.configuration.sections
      ) {
        console.error("Invalid data format");
        return;
      }

      let sections = decodedData?.configuration?.sections;
      let lastUpdatedIndex = sessionStorage.getItem("lastUpdatedIndex");
      lastUpdatedIndex =
        lastUpdatedIndex !== null ? Number(lastUpdatedIndex) : 0;

      if (lastUpdatedIndex + 1 >= sections.length) {
        return;
      }

      let ms = Number(sessionStorage.getItem("remainingTime")) + 60000;
      const totalSeconds = Math.floor(ms / 1000);
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
        2,
        "0"
      );
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      const timeToAdd = `${hours}:${minutes}:${seconds}`;

      function normalizeTimeFormat(time) {
        const parts = time.split(":").map(Number);
        if (parts.length === 2) {
          return `${String(parts[0]).padStart(2, "0")}:${String(
            parts[1]
          ).padStart(2, "0")}:00`;
        }
        return time;
      }

      function addTimes(time1, time2) {
        const [h1, m1, s1] = normalizeTimeFormat(time1).split(":").map(Number);
        const [h2, m2, s2] = normalizeTimeFormat(time2).split(":").map(Number);

        let totalSeconds = h1 * 3600 + m1 * 60 + s1 + h2 * 3600 + m2 * 60 + s2;
        let newHours = Math.floor(totalSeconds / 3600);
        let newMinutes = Math.floor((totalSeconds % 3600) / 60);
        let newSeconds = totalSeconds % 60;
        return `${String(newHours).padStart(2, "0")}:${String(
          newMinutes
        ).padStart(2, "0")}:${String(newSeconds).padStart(2, "0")}`;
      }

      function subtractTimes(time1, time2) {
        const [h1, m1, s1] = normalizeTimeFormat(time1).split(":").map(Number);
        const [h2, m2, s2] = normalizeTimeFormat(time2).split(":").map(Number);

        let totalSeconds =
          h1 * 3600 + m1 * 60 + s1 - (h2 * 3600 + m2 * 60 + s2);
        if (totalSeconds < 0) totalSeconds = 0;

        let newHours = Math.floor(totalSeconds / 3600);
        let newMinutes = Math.floor((totalSeconds % 3600) / 60);
        let newSeconds = totalSeconds % 60;

        return `${String(newHours).padStart(2, "0")}:${String(
          newMinutes
        ).padStart(2, "0")}:${String(newSeconds).padStart(2, "0")}`;
      }

      sections[lastUpdatedIndex].sectionTime = normalizeTimeFormat(
        sections[lastUpdatedIndex].sectionTime
      );
      sections[lastUpdatedIndex + 1].sectionTime = normalizeTimeFormat(
        sections[lastUpdatedIndex + 1].sectionTime
      );
      if (lastUpdatedIndex >= 0) {
        sections[lastUpdatedIndex].sectionTime = subtractTimes(
          sections[lastUpdatedIndex].sectionTime,
          timeToAdd
        );
      }
      sections[lastUpdatedIndex + 1].sectionTime = addTimes(
        sections[lastUpdatedIndex + 1].sectionTime,
        timeToAdd
      );
      lastUpdatedIndex += 1;
      sessionStorage.setItem("lastUpdatedIndex", lastUpdatedIndex);
      const updatedEncodedData = await handleEncodeJson({
        encodeJSON: decodedData,
      });
      const convertedUpdatedEncodedData = JSON.stringify(updatedEncodedData);
      sessionStorage.setItem(
        "candidate_exam_details",
        convertedUpdatedEncodedData
      );
      const timeDuration = candidateTokenDetails?.configuration?.timeDuration;
      const [thours, tminutes] = timeDuration.split(":").map(Number);
      const totalMinutes = thours * 60 + tminutes;
      if (Number(totalMinutes) - 2 >= Number(timeLeft?.totalMinutes)) {
        sessionStorage.setItem("isEarlyMovedSection", true);
      }
      window.dispatchEvent(new Event("storageUpdate"));
    }

    handleNext("next");
    handleClose();
    auditLog({
      data: {
        RegistrationNumber: `${candidateTokenDetails?.user?.id}`,
        RollNumber: `${candidateTokenDetails?.user?.rollNo}`,
        Activity: `User Moved to the Next Section Completed the Previous ${currentSectionTitle} in Live Test`,
        Timestamp: `${heartBeat?.date}`,
      },
      dispatch,
      heartBeat,
    });
  }

  return (
    <>
      <Dialog
        open={
          showModal?.show &&
          showModal?.method === "S" &&
          showModal?.type === type
        }
        onClose={handleClose}
        fullWidth
        maxWidth="sm"     
        sx={{
          "& .MuiDialog-paper": {
          borderRadius: "12px",
          padding: { xs: "12px", sm: "20px" }, // ✅ responsive padding
        },
      }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {type === "submit" && (
            <>
              <Grid sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    pb: 2,
                    fontWeight: "bold",
                  }}
                >
                  Are you sure you want to submit the exam?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, 
                    gap: "12px",
                    justifyContent: "center",
                    alignItems: "center",
                    pb: 2,
                  }}
                >
                  <SecondaryButton width={"200px"} onClick={handleSummary}>
                    CONTINUE TO SUBMIT
                  </SecondaryButton>
                  <PrimaryButton
                    width="200px"
                    padding={1}
                    color="#000"
                    borderRadius="138px"
                    onClick={handleClose}
                  >
                    GO BACK TO REVIEW
                  </PrimaryButton>
                </Box>
              </Grid>
            </>
          )}

          {type === "confirm" && (
            <>
              <Grid sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    pb: 2,
                    fontWeight: "bold",
                  }}
                >
                  This Action cannot be changed.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                    pb: 2,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    style={{
                      cursor: "default",
                      borderRadius: "78px",
                      fontWeight: "bold",
                      cursor: "default",
                      width: "200px",
                    }}
                    onClick={handleSubmit}
                  >
                    SUBMIT EXAM
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      width: "200px",
                      cursor: "default",
                      backgroundColor: "#E598AC",
                      color: "#000",
                      borderRadius: "138px",
                      height: "38px",
                      "&:hover": {
                        backgroundColor: "#E598AC",
                      },
                    }}
                    onClick={handleClose}
                  >
                   CANCEL
                  </Button>
                </Box>
              </Grid>
            </>
          )}

          {type === "inactive" && (
            <>
              <Grid sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    pb: 2,
                    fontWeight: "bold",
                  }}
                >
                  You seem to be idle, continue?
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                    pb: 2,
                  }}
                >
                  <SecondaryButton width={"100%"} onClick={handleYes}>
                    Yes
                  </SecondaryButton>
                </Box>
              </Grid>
            </>
          )}

          {type === "nextsection" && (
            <>
              <Grid sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "20px",
                    pb: 2,
                    fontWeight: "bold",
                  }}
                >
                  Please confirm your submission. After proceeding, you will not
                  be able to return to this section.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                    pb: 2,
                  }}
                >
                  <SecondaryButton
                    width={"100%"}
                    onClick={handleYesNextSection}
                  >
                    Yes
                  </SecondaryButton>
                  <PrimaryButton
                    width="100%"
                    color="#000"
                    borderRadius="138px"
                    height="38px"
                    onClick={handleClose}
                  >
                    No
                  </PrimaryButton>
                </Box>
              </Grid>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
